import { useEffect, useMemo } from 'react';
import client from 'src/gatsby-plugin-apollo/client';
import create from 'zustand';
import { persist } from 'zustand/middleware';
import { StringParam, useQueryParams } from 'use-query-params';
import {
  useQuery,
  useMutation,
  GET_SESSION,
  GET_OPEN_ENROLLMENT_DATES,
  UPSERT_SESSION,
  REPLACE_DEPENDENTS,
} from 'src/data';
import { Segment, SegmentEvent } from 'src/lib';
import { ENV, getDefaultExchangeType } from 'src/utils';
import { getCounty } from './useZipcode';
import { useParams } from './useParams';
import { getReplacePayload } from 'src/utils/explorer';
import { useAnonymousProfile } from 'src/utils/useAnonymousProfile';

export interface PublicSessionStore {
  sessionID?: string;
  zip?: string;
  /** a zip we couldn't resolve to a county; not persisted, so a reload retries the lookup */
  invalidZip?: string;
  setSession: (pxid: string) => void;
  setZip: (zip: string) => void;
  markZipInvalid: (zip: string) => void;
  clearSession: () => void;
}

export const useSession = create(
  persist<PublicSessionStore>(
    (set) => ({
      sessionID: undefined,
      zip: undefined,
      invalidZip: undefined,

      setSession: (sessionID) => set({ sessionID }),
      setZip: (zip) => set((state) => (zip === state.zip ? {} : { zip, invalidZip: undefined })),
      markZipInvalid: (invalidZip) => set({ invalidZip }),

      clearSession: () => set({ sessionID: undefined }),
    }),
    {
      name: 'public_session',
      partialize: ({ invalidZip: _invalidZip, ...state }) => state,
      onRehydrateStorage: () => (state) => {
        const stored = localStorage.getItem('catch_pxid');

        if (stored) {
          if (state) state?.setSession(stored);
          localStorage.removeItem('catch_pxid'); // Optional: remove after transferring
        }
      },
    },
  ),
);

export const usePublicSession = (root?: boolean) => {
  const { partner } = useAnonymousProfile();
  const params = useParams();
  const [query] = useQueryParams({ sid: StringParam, zip: StringParam });
  const { sessionID, zip, invalidZip, setZip, markZipInvalid, setSession, clearSession } =
    useSession();
  const initializing = (!!query.zip && !zip) || (!!query.sid && !sessionID);

  useEffect(() => {
    if (query.zip) setZip(query.zip);
    if (query.sid) setSession(query.sid);
  }, [query]);

  /**
   * If there is no session ID (either from query or storage)
   * but there is a defined zip, we should create a new session.
   * Skipped for a zip we couldn't resolve, so loading ends and the page can send the
   * user back to the zip step instead of waiting on a session that will never exist.
   */
  const shouldCreateSession = useMemo(() => {
    return !sessionID && !!zip && zip !== invalidZip;
  }, [sessionID, zip, invalidZip]);

  useQuery(GET_SESSION, {
    client,
    variables: { id: sessionID || '' },
    skip: !sessionID,
    onError: () => clearSession(),
    fetchPolicy: 'cache-first',
  });

  const [replaceDependents] = useMutation(REPLACE_DEPENDENTS, { client });

  // The new id is published by the caller once the session is complete, not
  // from onCompleted: publishing it before the household is saved lets
  // PlanDetails run against an explorer with no members (E-8756).
  const [upsertSession, { loading: upserting }] = useMutation(UPSERT_SESSION, { client });

  const storeNewSession = (newID: string) => {
    if (newID !== sessionID) {
      setSession(newID);

      Segment.track(SegmentEvent.PX_SESSION_CREATED, {
        pxId: newID,
        adminPxLink: `${ENV.adminUrl}/explorer/${newID}`,
      });
    }
  };

  const createNewSession = async (zip: string) => {
    const [county, oe] = await Promise.all([
      getCounty(zip).catch(() => undefined),
      client.query({ query: GET_OPEN_ENROLLMENT_DATES, fetchPolicy: 'cache-first' }),
    ]);
    const year = oe.data?.reference?.health?.openEnrollmentDates?.preferredCoverageYear;

    // state, countyfips and zipcode are required; without a resolved county the upsert fails.
    if (!county?.state || !county?.fips || !county?.zipcode) {
      markZipInvalid(zip);
      return;
    }

    const result = await upsertSession({
      variables: {
        input: {
          coverageYear: params.year || year,
          state: county?.state,
          countyfips: county?.fips,
          zipcode: county?.zipcode,
          income: params.income,
          exchangeType: getDefaultExchangeType({ partner })
        },
      },
    });
    const explorerDataID = result?.data?.upsertPublicHealthExplorerData?.id;

    // A new session has no members yet, so there is nothing to carry over.
    // useSyncParamsToSession then finds the params already applied and does not write again.
    if (explorerDataID && params.applicants?.length) {
      await replaceDependents({
        variables: {
          input: {
            explorerDataID,
            publicHealthExplorerDependentInputs: getReplacePayload(params.applicants),
          },
        },
        refetchQueries: ['PublicHealthSession'],
      });
    }

    if (explorerDataID) storeNewSession(explorerDataID);
  };

  useEffect(() => {
    if (shouldCreateSession && root && zip) {
      createNewSession(zip);
    }
  }, [shouldCreateSession, root]);

  return {
    loading: shouldCreateSession || initializing,
    sessionID,
    upsertSession,
    storeNewSession,
    upserting,
  };
};
